import DOMPurify from 'dompurify';
import { decode } from 'he';

import { type INavigationItem } from '../../common/services/NavigationService';

export class SubHeaderNavItemAdapter {
  static create(i: INavigationItem): ISubHeaderNavItem {
    return {
      ...i,
      focused: false,
      open: false,
      accent: getAccentByItem(i),
      navTeaserSelector: getSelectorByItem(i),
      children: i.children ? i.children.map(c => SubHeaderNavItemAdapter.create(c)) : [],
    };
  }
}

export enum EKeyCode {
  ArrowRight = 'ArrowRight',
  ArrowUp = 'ArrowUp',
  ArrowLeft = 'ArrowLeft',
  ArrowDown = 'ArrowDown',
  Tab = 'Tab',
  Escape = 'Escape',
  Enter = 'Enter',
  Space = 'Space',
}

export enum EAccent {
  FIRST = 'first',
  SECOND = 'second',
  THIRD = 'third',
  FORTH = 'forth',
}

function getAccentByItem(i: INavigationItem) {
  switch (i.path) {
    case '/private-banking.html':
      return EAccent.FIRST;
    case '/verwaltersoftware.html':
      return EAccent.SECOND;
    case '/immobilienwirtschaft.html':
      return EAccent.THIRD;
    default:
      return EAccent.FORTH;
  }
}

function getSelectorByItem(i: INavigationItem) {
  switch (i.path) {
    case '/private-banking.html':
      return 'nav-teaser-private-banking';
    case '/verwaltersoftware.html':
      return 'nav-teaser-management-software';
    case '/immobilienwirtschaft.html':
      return 'nav-teaser-real-estate';
    case '/ihre-hausbank.html':
      return 'nav-teaser-hausbank';
    case '/spezialfinanzierung.html':
      return 'nav-teaser-special-financing';
    default:
      return '';
  }
}

export function getFirstFocusableElement(container: HTMLElement): HTMLElement | null {
  if (!container) return null;

  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ];

  const elements = Array.prototype.slice.call(
    container.querySelectorAll<HTMLElement>(focusableSelectors.join(','))
  );

  for (const el of elements) {
    if (isVisible(el)) {
      return el;
    }
  }

  return null;
}

function isVisible(el: HTMLElement): boolean {
  return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
}

export interface ISubHeaderNavItem extends INavigationItem {
  focused: boolean;
  open: boolean;
  accent: EAccent;
  navTeaserSelector: string;
  children: ISubHeaderNavItem[];
}
export function sanitize(str: string): string {
  return DOMPurify.sanitize(decode(str + ''));
}

export function mutateDeep<T extends { children?: T[] }>(
  nodes: T[],
  predicate: (node: T) => boolean,
  updater: (node: T) => void
): boolean {
  for (const node of nodes) {
    if (predicate(node)) {
      updater(node);
      return true; // Mutation done
    }
    if (node.children && mutateDeep(node.children, predicate, updater)) {
      return true;
    }
  }
  return false;
}
export * from '../../common/services/NavigationService';
export * from '../../common/services/NavTeaserService';
